import { Component, EventEmitter, OnDestroy, OnInit } from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { ActivatedRoute, Router } from '@angular/router';
import { takeUntil } from 'rxjs/operators';
import { Session } from '../../services/Session';
import { SwitchBoard } from '../../services/SwitchBoard';
import { StateService } from '../../services/StateService';
import { PageTitleService } from '../../services/PageTitle';
import { LoadingService } from '../../services/LoadingService';
import { ToastService } from '../../services/Toast.service';
import { TOAST_TYPE } from '../../constants/Tpiq.constants';
import { TaxPlanningService } from '../../tpiq-services/tax-planning.service';
import { TaxProjectionsService } from '../../tpiq-services/tax-projections.service';
import { ProjectionSet, ProjectionScenario, ProjectionFieldValue } from '../../models/projection.model';
import { ProjectionGridComponent } from './projection-grid/projection-grid.component';
import { PROJECTION_FIELDS } from '../../constants/projection-field-definitions';

@Component({
  selector: 'app-projection-detail',
  standalone: true,
  imports: [CommonModule, FormsModule, ProjectionGridComponent],
  templateUrl: './projection-detail.component.html',
  styleUrl: './projection-detail.component.scss'
})
export class ProjectionDetailComponent implements OnInit, OnDestroy {
  org: string;
  user: any;
  clientId: string;
  projectionSetId: string;
  destroy$: EventEmitter<void> = new EventEmitter<void>();

  projectionSet: ProjectionSet;
  clientData: any;
  ocrDocuments: any[] = [];
  selectedOcrFileId: string = '';
  projectionYear: number = new Date().getFullYear();
  projectionName: string = 'Tax Projection';
  recalcTrigger: number = 0;

  availableYears: number[] = [];

  // Debounce timer for metadata save
  private metadataSaveTimer: any;

  constructor(
    private _router: Router,
    private _route: ActivatedRoute,
    private switchBoard: SwitchBoard,
    private stateService: StateService,
    private titleService: PageTitleService,
    private loadingService: LoadingService,
    private toastService: ToastService,
    private taxPlanningService: TaxPlanningService,
    private taxProjectionsService: TaxProjectionsService
  ) {}

  ngOnInit(): void {
    this.titleService.setPageTitle('');
    this.user = Session.getUser();
    this.org = Session.getOrg();

    const currentYear = new Date().getFullYear();
    for (let y = currentYear - 2; y <= currentYear + 2; y++) {
      this.availableYears.push(y);
    }

    this._route.params.pipe(takeUntil(this.destroy$)).subscribe(params => {
      this.clientId = params.clientId;
      this.projectionSetId = params.projectionSetId;
      this.loadProjectionSet();
      this.loadClientData();
      this.loadOcrDocuments();
    });

    this.switchBoard.onPageChangeListener.next({
      url: this._route.url,
      name: 'Tax Projection',
      id: 'TAX_PROJECTION'
    });

    this.switchBoard.onClickPrev.pipe(takeUntil(this.destroy$)).subscribe(() => {
      this.handleBackButton();
    });
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.unsubscribe();
    if (this.metadataSaveTimer) clearTimeout(this.metadataSaveTimer);
  }

  loadProjectionSet(): void {
    this.loadingService.triggerLoadingEvent(true);
    this.taxProjectionsService.getProjectionSet(this.org, this.user.id, this.clientId, this.projectionSetId)
      .subscribe(
        response => {
          this.loadingService.triggerLoadingEvent(false);
          if (response) {
            this.projectionSet = response;
            this.projectionName = response.name;
            this.projectionYear = response.projectionYear;
            this.selectedOcrFileId = response.sourceOcrFileId || '';
          }
        },
        error => {
          this.loadingService.triggerLoadingEvent(false);
          this.toastService.pop(TOAST_TYPE.error, 'Failed to load projection');
        }
      );
  }

  loadClientData(): void {
    this.taxPlanningService.getClient(this.org, this.user.id, this.clientId, false)
      .subscribe(response => {
        this.clientData = response;
      });
  }

  loadOcrDocuments(): void {
    this.taxProjectionsService.getOcrDocuments(this.org, this.user.id, this.clientId)
      .subscribe(
        response => {
          this.ocrDocuments = response || [];
        },
        error => {
          this.ocrDocuments = [];
        }
      );
  }

  onProjectionYearChange(): void {
    if (this.projectionSet) {
      this.projectionSet.projectionYear = this.projectionYear;
      this.debouncedMetadataSave();
      this.recalcTrigger++;
    }
  }

  onOcrDocumentChange(): void {
    if (!this.projectionSet || !this.selectedOcrFileId) return;

    this.loadingService.triggerLoadingEvent(true);
    this.taxProjectionsService.importOcrData(
      this.org, this.user.id, this.clientId, this.projectionSet.id, this.selectedOcrFileId
    ).subscribe(
      (response: any) => {
        this.loadingService.triggerLoadingEvent(false);
        if (response && response.status === 'Warning') {
          this.toastService.pop(TOAST_TYPE.warning, response.message || 'No extractable 1040 data found in this document');
        } else {
          this.toastService.pop(TOAST_TYPE.success, 'OCR data imported successfully');
        }
        // Reload the full projection set to get updated field values
        this.loadProjectionSet();
      },
      error => {
        this.loadingService.triggerLoadingEvent(false);
        this.toastService.pop(TOAST_TYPE.error, 'Failed to import OCR data');
      }
    );
  }

  onFilingStatusChanged(filingStatus: string): void {
    if (this.projectionSet) {
      this.projectionSet.filingStatus = filingStatus;
      this.debouncedMetadataSave();
      this.recalcTrigger++;
    }
  }

  onHomeStateBlur(): void {
    if (this.projectionSet) {
      this.debouncedMetadataSave();
      this.recalcTrigger++;
    }
  }

  onProjectionNameBlur(): void {
    if (this.projectionSet) {
      this.projectionSet.name = this.projectionName;
      this.debouncedMetadataSave();
    }
  }

  deleteScenario(scenarioId: string): void {
    if (!this.projectionSet || !this.projectionSet.scenarios) return;
    const scenario = this.projectionSet.scenarios.find(s => s.id === scenarioId);
    if (!scenario || scenario.type !== 'scenario') return;

    this.taxProjectionsService.deleteScenario(this.org, this.user.id, this.clientId, this.projectionSet.id, scenarioId)
      .subscribe(
        () => {
          this.projectionSet.scenarios = this.projectionSet.scenarios.filter(s => s.id !== scenarioId);
          this.toastService.pop(TOAST_TYPE.success, 'Scenario deleted');
        },
        error => {
          this.toastService.pop(TOAST_TYPE.error, 'Failed to delete scenario');
        }
      );
  }

  addScenario(): void {
    if (!this.projectionSet || !this.projectionSet.scenarios) return;
    const scenarioCount = this.projectionSet.scenarios.filter(s => s.type === 'scenario').length;
    const newScenario: Partial<ProjectionScenario> = {
      name: 'Scenario ' + (scenarioCount + 1),
      type: 'scenario',
      sortOrder: this.projectionSet.scenarios.length,
    };

    this.taxProjectionsService.addScenario(this.org, this.user.id, this.clientId, this.projectionSet.id, newScenario)
      .subscribe(
        response => {
          if (response) {
            response.fieldValues = [];
            this.projectionSet.scenarios.push(response);
          }
        },
        error => {
          this.toastService.pop(TOAST_TYPE.error, 'Failed to add scenario');
        }
      );
  }

  private debouncedMetadataSave(): void {
    if (this.metadataSaveTimer) clearTimeout(this.metadataSaveTimer);
    this.metadataSaveTimer = setTimeout(() => {
      this.taxProjectionsService.updateProjectionSet(
        this.org, this.user.id, this.clientId, this.projectionSet.id,
        {
          name: this.projectionSet.name,
          projectionYear: this.projectionSet.projectionYear,
          sourceOcrFileId: this.projectionSet.sourceOcrFileId,
          filingStatus: this.projectionSet.filingStatus,
          homeState: this.projectionSet.homeState,
          status: this.projectionSet.status
        }
      ).subscribe(
        () => {},
        error => {
          this.toastService.pop(TOAST_TYPE.error, 'Failed to save projection changes');
        }
      );
    }, 500);
  }

  handleBackButton(): void {
    const prevState = this.stateService.getPrevState();
    if (prevState) {
      this.stateService.pop();
      this._router.navigate([prevState.url]);
    } else {
      this._router.navigate(['tax-planning', 'clients', this.clientId, 'view']);
    }
  }

  navigateBack(): void {
    this._router.navigate(['tax-planning', 'clients', this.clientId, 'view']);
  }
}
